/**
 * TemplateUpdater — create / edit an email template (opened from TemplatesManager).
 *
 * Two editors on the same HTML (form.htmlBody), kept in sync, shown one at a time via a
 * switch (default: visual):
 *   - "HTML" textarea (raw)
 *   - visual editor: a contentEditable copy; right-click selected text for bold / italic /
 *     underline / color / size / alignment (document.execCommand), right-click an image to
 *     change its URL. Changes are written back to htmlBody on blur / after each command.
 * Attachments can be added only after the template exists (edit mode).
 *
 * Data (unchanged): createEmailTemplate / updateEmailTemplate · getTemplateAttachments ·
 * uploadTemplateAttachment · deleteTemplateAttachment. Preview opens TemplatePreview.
 * Props: flag 'add' | 'update', template (for update), onClose(refresh: bool)
 *
 * UI only changed in the redesign (Ant dialog, chips, restyled context menu rendered in a
 * portal so it isn't offset by the dialog's transform). Clicking outside no longer closes it.
 */
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import {
  EyeOutlined,
  EditOutlined,
  CodeOutlined,
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  BgColorsOutlined,
  FontSizeOutlined,
  AlignRightOutlined,
  AlignCenterOutlined,
  AlignLeftOutlined,
  PictureOutlined,
  RightOutlined,
} from "@ant-design/icons";
import * as actionsCreators from "./../../../actions/index.js";
import { Modal, Button, Input, Field, Segmented } from "../../../ui";
import TemplatePreview from "./TemplatePreview";
import AttachmentPicker from "./../../../components/AttachmentPicker";

import classes from "./TemplateUpdater.css";

// Merge fields the server fills in per customer
const VARIABLES = ["{{firstname}}", "{{lastname}}", "{{fullname}}", "{{mail}}", "{{phone}}", "{{status}}"];

const COLORS = [
  { name: 'שחור', value: '#000000' },
  { name: 'אדום', value: '#ff0000' },
  { name: 'כחול', value: '#0000ff' },
  { name: 'ירוק', value: '#008000' },
  { name: 'כתום', value: '#ff8c00' },
  { name: 'סגול', value: '#800080' },
  { name: 'אפור', value: '#808080' },
];

const FONT_SIZES = [
  { name: 'קטן מאוד', value: '1' },
  { name: 'קטן', value: '2' },
  { name: 'רגיל', value: '3' },
  { name: 'בינוני', value: '4' },
  { name: 'גדול', value: '5' },
  { name: 'גדול מאוד', value: '6' },
  { name: 'ענק', value: '7' },
];

const TemplateUpdater = (props) => {
  const [form, setForm] = useState({
    name: "",
    subject: "",
    tag: "",
    htmlBody: "",
    textBody: "",
  });
  const [showPreview, setShowPreview] = useState(false);
  const [activeEditor, setActiveEditor] = useState(null); // 'html' or 'preview'
  const previewRef = useRef(null);
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0 });
  const [activeSubmenu, setActiveSubmenu] = useState(null); // 'color', 'fontSize', 'align'
  const savedSelectionRef = useRef(null);
  const editingImageRef = useRef(null); // Store reference to image being edited
  const [imageUrlModal, setImageUrlModal] = useState({ show: false, url: '' });
  const [attachments, setAttachments] = useState([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  // Which editor is shown (UI only). Both stay mounted — the hidden one is display:none —
  // so the HTML ⇄ visual sync below keeps working exactly as before.
  const [editorMode, setEditorMode] = useState("visual"); // "visual" | "html"

  useEffect(() => {
    if (props.flag === "update" && props.template) {
      setForm({
        name: props.template.name || "",
        subject: props.template.subject || "",
        tag: props.template.tag || "",
        htmlBody: props.template.htmlBody || "",
        textBody: props.template.textBody || "",
      });
      // Load existing attachments for this template
      loadTemplateAttachments(props.template.id);
    }
  }, [props.template, props.flag]);

  const loadTemplateAttachments = async (templateId) => {
    try {
      const templateAttachments = await props.getTemplateAttachments(templateId);
      setAttachments(templateAttachments || []);
    } catch (error) {
      console.error("Error loading template attachments:", error);
    }
  };

  const handleAddAttachment = async (file) => {
    if (props.flag !== "update" || !props.template) {
      props.showAlert("יש לשמור את התבנית קודם לפני הוספת קבצים מצורפים");
      return;
    }

    setAttachmentUploading(true);
    try {
      const result = await props.uploadTemplateAttachment(props.template.id, file);
      if (result.success && result.uploaded && result.uploaded.length > 0) {
        setAttachments(prev => [...prev, ...result.uploaded]);
      } else if (result.errors && result.errors.length > 0) {
        props.showAlert(result.errors[0].error);
      }
    } catch (error) {
      console.error("Upload error:", error);
      props.showAlert("שגיאה בהעלאת הקובץ");
    }
    setAttachmentUploading(false);
  };

  const handleRemoveAttachment = async (id) => {
    if (!props.template) return;

    try {
      await props.deleteTemplateAttachment(props.template.id, id);
      setAttachments(prev => prev.filter(a => a.id !== id));
    } catch (error) {
      console.error("Delete error:", error);
      props.showAlert("שגיאה במחיקת הקובץ");
    }
  };

  // Sync HTML to preview when editing in HTML editor
  useEffect(() => {
    if (previewRef.current && activeEditor !== 'preview') {
      previewRef.current.innerHTML = form.htmlBody;
    }
  }, [form.htmlBody, activeEditor]);

  // Close context menu on click outside
  useEffect(() => {
    const handleClickOutside = () => {
      // Clean up any marked images (only if modal is not open)
      if (!imageUrlModal.show && previewRef.current) {
        const markedImage = previewRef.current.querySelector('[data-editing-image="true"]');
        if (markedImage) {
          markedImage.removeAttribute('data-editing-image');
        }
        editingImageRef.current = null;
      }
      setContextMenu({ show: false, x: 0, y: 0 });
      setActiveSubmenu(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [imageUrlModal.show]);

  // Save current selection
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  // Restore saved selection
  const restoreSelection = () => {
    if (savedSelectionRef.current && previewRef.current) {
      // Focus the editable element first
      previewRef.current.focus();
      // Then restore the selection
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);
    }
  };

  // Handle right-click on preview
  const handleContextMenu = (e) => {
    const target = e.target;

    // Clear any previous image selection marker
    if (previewRef.current) {
      const previouslyMarked = previewRef.current.querySelector('[data-editing-image="true"]');
      if (previouslyMarked) {
        previouslyMarked.removeAttribute('data-editing-image');
      }
    }

    // Check if clicked on an image
    if (target.tagName === 'IMG') {
      e.preventDefault();
      // Mark this image for editing
      target.setAttribute('data-editing-image', 'true');
      editingImageRef.current = target; // Store direct reference
      setContextMenu({ show: true, x: e.clientX, y: e.clientY, isImage: true, imageUrl: target.src });
      setActiveSubmenu(null);
      return;
    }

    // Check for text selection
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      e.preventDefault();
      saveSelection();
      setContextMenu({ show: true, x: e.clientX, y: e.clientY, isImage: false });
      setActiveSubmenu(null);
    }
  };

  // Prevent menu from stealing focus
  const handleMenuMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Apply formatting command
  const applyFormat = (command, value = null) => {
    restoreSelection();
    // Small delay to ensure focus is set
    setTimeout(() => {
      document.execCommand(command, false, value);
      // Sync changes to form
      if (previewRef.current) {
        handleChange("htmlBody", previewRef.current.innerHTML);
      }
    }, 0);
    setContextMenu({ show: false, x: 0, y: 0 });
    setActiveSubmenu(null);
  };

  // Handle image URL change
  const handleSetImageUrl = () => {
    setImageUrlModal({ show: true, url: contextMenu.imageUrl || '' });
    setContextMenu({ show: false, x: 0, y: 0 });
    setActiveSubmenu(null);
  };

  const handleImageUrlSave = () => {
    if (previewRef.current && imageUrlModal.url) {
      // Try to use the stored ref first, fall back to querySelector
      let targetImage = editingImageRef.current;
      if (!targetImage || !previewRef.current.contains(targetImage)) {
        targetImage = previewRef.current.querySelector('[data-editing-image="true"]');
      }

      if (targetImage) {
        targetImage.src = imageUrlModal.url;
        targetImage.removeAttribute('data-editing-image');
        // Sync changes to form immediately
        const newHtml = previewRef.current.innerHTML;
        setForm(prev => ({ ...prev, htmlBody: newHtml }));
      }
    }
    editingImageRef.current = null;
    setImageUrlModal({ show: false, url: '' });
  };

  // Cancel the image-URL dialog: unmark the image that was being edited
  const cancelImageUrl = () => {
    if (previewRef.current) {
      const markedImage = previewRef.current.querySelector('[data-editing-image="true"]');
      if (markedImage) {
        markedImage.removeAttribute('data-editing-image');
      }
    }
    editingImageRef.current = null;
    setImageUrlModal({ show: false, url: '' });
  };

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleSave = async () => {
    if (!form.name || !form.subject) {
      props.showAlert("נא למלא שם ונושא");
      return;
    }

    if (!form.htmlBody) {
      props.showAlert("נא למלא את תוכן האימייל");
      return;
    }

    try {
      if (props.flag === "add") {
        await props.createEmailTemplate(form);
        props.showAlert("התבנית נוצרה בהצלחה");
      } else {
        await props.updateEmailTemplate(props.template.id, form);
        props.showAlert("התבנית עודכנה בהצלחה");
      }
      props.onClose(true);
    } catch (error) {
      console.error("Error saving template:", error);
      props.showAlert("שגיאה בשמירת התבנית");
    }
  };

  const getTitle = () => {
    return props.flag === "add" ? "תבנית אימייל חדשה" : "עריכת תבנית אימייל";
  };

  const handlePreview = () => {
    if (props.flag === "update" && props.template) {
      setShowPreview(true);
    } else {
      props.showAlert("נא לשמור את התבנית תחילה כדי לראות תצוגה מקדימה");
    }
  };

  return (
    <div>
      {showPreview && props.template && (
        <TemplatePreview
          templateId={props.template.id}
          templateName={props.template.name}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Image URL dialog (right-click an image in the visual editor) */}
      <Modal
        open={imageUrlModal.show}
        title="שינוי כתובת תמונה"
        okText="שמור"
        cancelText="ביטול"
        onOk={handleImageUrlSave}
        onCancel={cancelImageUrl}
        zIndex={1100}
      >
        <Field label="כתובת התמונה (URL)">
          <Input
            dir="ltr"
            value={imageUrlModal.url}
            onChange={(e) => setImageUrlModal({ ...imageUrlModal, url: e.target.value })}
            placeholder="https://example.com/image.jpg"
          />
        </Field>
      </Modal>

      <Modal
        open
        width={1180}
        style={{ top: 24 }}
        maskClosable={false}
        title={getTitle()}
        onCancel={() => props.onClose(false)}
        footer={
          <div className={classes.footer}>
            {props.flag === "update" ? (
              <Button icon={<EyeOutlined />} onClick={handlePreview}>
                תצוגה מקדימה
              </Button>
            ) : null}
            <span className={classes.spacer} />
            <Button onClick={() => props.onClose(false)}>ביטול</Button>
            <Button type="primary" onClick={handleSave}>
              שמור
            </Button>
          </div>
        }
      >
        <div className={classes.formContainer}>
          <div className={classes.topFields}>
            <Field label="שם התבנית" required>
              <Input value={form.name} onChange={(e) => handleChange("name", e.target.value)} placeholder="לדוגמה: ברוכים הבאים" />
            </Field>
            <Field label="תגית (קטגוריה)">
              <Input value={form.tag} onChange={(e) => handleChange("tag", e.target.value)} placeholder="ניוזלטר, פרסומת, עדכון…" />
            </Field>
            <Field label="נושא האימייל" required>
              <Input value={form.subject} onChange={(e) => handleChange("subject", e.target.value)} placeholder="לדוגמה: שלום {{firstname}}" />
            </Field>
          </div>

          <div className={classes.variables}>
            <span className={classes.variablesLabel}>משתנים זמינים:</span>
            {VARIABLES.map((v) => (
              <code key={v} className={classes.variable} dir="ltr">
                {v}
              </code>
            ))}
          </div>

          <div className={classes.editorHeader}>
            <Segmented
              value={editorMode}
              onChange={setEditorMode}
              options={[
                { value: "visual", label: "עריכה חזותית", icon: <EditOutlined /> },
                { value: "html", label: "HTML", icon: <CodeOutlined /> },
              ]}
            />
            {editorMode === "visual" ? (
              <span className={classes.panelHint}>לחצו לעריכה · קליק ימני על טקסט מסומן לעיצוב, על תמונה לשינוי כתובת</span>
            ) : null}
          </div>

          <div className={classes.editorsContainer}>
            <div className={classes.editorPanel} style={editorMode === "html" ? undefined : { display: "none" }}>
              <Input.TextArea
                value={form.htmlBody}
                onChange={(e) => handleChange("htmlBody", e.target.value)}
                onFocus={() => setActiveEditor('html')}
                placeholder={'<h1>שלום {{firstname}}</h1><p>תוכן האימייל...</p>'}
                className={classes.htmlTextarea}
                dir="ltr"
              />
            </div>

            <div className={classes.editorPanel} style={editorMode === "visual" ? undefined : { display: "none" }}>
              <div
                ref={previewRef}
                className={classes.editablePreview}
                contentEditable={true}
                suppressContentEditableWarning={true}
                onFocus={() => setActiveEditor('preview')}
                onBlur={() => {
                  if (previewRef.current) {
                    handleChange("htmlBody", previewRef.current.innerHTML);
                  }
                  setActiveEditor(null);
                }}
                onContextMenu={handleContextMenu}
              />
            </div>
          </div>

          {/* AttachmentPicker shows its own "קבצים מצורפים" title */}
          <Field hint={props.flag === "add" ? "שמרו את התבנית תחילה כדי להוסיף קבצים" : null}>
            <AttachmentPicker
              attachments={attachments}
              onAdd={handleAddAttachment}
              onRemove={handleRemoveAttachment}
              uploading={attachmentUploading}
              disabled={props.flag === "add"}
            />
          </Field>
        </div>
      </Modal>

      {/* Formatting context menu — in a portal (position: fixed relative to the page) */}
      {contextMenu.show &&
        ReactDOM.createPortal(
          <div
            className={classes.contextMenu}
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={handleMenuMouseDown}
          >
            {contextMenu.isImage ? (
              <div className={classes.contextMenuItem} onClick={handleSetImageUrl}>
                <PictureOutlined /> שנה כתובת תמונה
              </div>
            ) : (
              <>
                <div className={classes.contextMenuItem} onClick={() => applyFormat('bold')}>
                  <BoldOutlined /> מודגש
                </div>
                <div className={classes.contextMenuItem} onClick={() => applyFormat('italic')}>
                  <ItalicOutlined /> נטוי
                </div>
                <div className={classes.contextMenuItem} onClick={() => applyFormat('underline')}>
                  <UnderlineOutlined /> קו תחתון
                </div>

                <div className={classes.contextMenuDivider} />

                <div className={classes.contextMenuItem} onMouseEnter={() => setActiveSubmenu('color')}>
                  <BgColorsOutlined /> צבע טקסט <RightOutlined className={classes.submenuArrow} />
                  {activeSubmenu === 'color' && (
                    <div className={classes.submenu}>
                      {COLORS.map((color) => (
                        <div key={color.value} className={classes.contextMenuItem} onClick={() => applyFormat('foreColor', color.value)}>
                          <span className={classes.colorDot} style={{ background: color.value }} /> {color.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className={classes.contextMenuItem} onMouseEnter={() => setActiveSubmenu('fontSize')}>
                  <FontSizeOutlined /> גודל טקסט <RightOutlined className={classes.submenuArrow} />
                  {activeSubmenu === 'fontSize' && (
                    <div className={classes.submenu}>
                      {FONT_SIZES.map((size) => (
                        <div key={size.value} className={classes.contextMenuItem} onClick={() => applyFormat('fontSize', size.value)}>
                          {size.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className={classes.contextMenuItem} onMouseEnter={() => setActiveSubmenu('align')}>
                  <AlignRightOutlined /> יישור <RightOutlined className={classes.submenuArrow} />
                  {activeSubmenu === 'align' && (
                    <div className={classes.submenu}>
                      <div className={classes.contextMenuItem} onClick={() => applyFormat('justifyRight')}>
                        <AlignRightOutlined /> ימין
                      </div>
                      <div className={classes.contextMenuItem} onClick={() => applyFormat('justifyCenter')}>
                        <AlignCenterOutlined /> מרכז
                      </div>
                      <div className={classes.contextMenuItem} onClick={() => applyFormat('justifyLeft')}>
                        <AlignLeftOutlined /> שמאל
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      createEmailTemplate: actionsCreators.createEmailTemplate,
      updateEmailTemplate: actionsCreators.updateEmailTemplate,
      showAlert: actionsCreators.showAlert,
      getTemplateAttachments: actionsCreators.getTemplateAttachments,
      uploadTemplateAttachment: actionsCreators.uploadTemplateAttachment,
      deleteTemplateAttachment: actionsCreators.deleteTemplateAttachment,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(TemplateUpdater);
